'use client';

import { c as _c } from "react/compiler-runtime";
import { Radio } from '@common/components';
import { cn } from '@core/utils';
import type { ListGiftOrderHistoryDto } from '@generated/auth';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { AuthQueryOptions } from '@/api';
import { usePhotoReviewEvent } from '@/app/donation/_hooks/usePhotoReviewEvent/usePhotoReviewEvent';
import { ORDER_STATUS, ORDER_STATUS_LABEL } from '@/app/donation/constants';
import { PageNavigation } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { WEBVIEW_TYPE_ENTRY } from '@/constants';
import { useWebviewTypeEntry } from '@/hooks/useWebviewTypeEntry';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { DesktopTitle } from '../_components/DesktopTitle';
import { EmptyState } from './_components/EmptyState';
import { Filter } from './_components/Filter';
import { OrderCard } from './_components/OrderCard';
import { RecommendPhotoReviewModal } from './_components/RecommendPhotoReviewModal';
const PERIOD_OPTIONS = [{
  value: 'today',
  label: '오늘'
}, {
  value: 'week',
  label: '1주일'
}, {
  value: 'month',
  label: '1개월'
}, {
  value: 'sixMonths',
  label: '6개월'
}, {
  value: 'year',
  label: '1년'
}];
const STATUS_OPTIONS = [{
  value: 'all',
  label: '전체주문'
}, {
  value: ORDER_STATUS.PAYMENT_COMPLETE,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.PAYMENT_COMPLETE]
}, {
  value: ORDER_STATUS.DELIVERY_PREPARING,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.DELIVERY_PREPARING]
}, {
  value: ORDER_STATUS.PURCHASE_CONFIRMED,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.PURCHASE_CONFIRMED]
}, {
  value: ORDER_STATUS.EXCHANGE_AND_RETURN,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.EXCHANGE_AND_RETURN]
}, {
  value: ORDER_STATUS.CANCELLED,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.CANCELLED]
}];
const OrdersPage = () => {
  const $ = _c(64);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [isStatusDrawerOpen, setIsStatusDrawerOpen] = useState(false);
  const [isPeriodDrawerOpen, setIsPeriodDrawerOpen] = useState(false);
  const [isRecommendPhotoReviewModalOpen, setIsRecommendPhotoReviewModalOpen] = useState(false);
  const {
    entry
  } = useWebviewTypeEntry();
  const {
    isEventProcess
  } = usePhotoReviewEvent();
  const webviewType = useDeviceInfoStore(_temp);
  let t0;
  let t1;
  if ($[0] !== selectedPeriod) {
    const today = dayjs();
    const endDate = today.format("YYYY-MM-DD");
    const startDate = {
      today: today.format("YYYY-MM-DD"),
      week: today.subtract(7, "day").format("YYYY-MM-DD"),
      month: today.subtract(1, "month").format("YYYY-MM-DD"),
      sixMonths: today.subtract(6, "month").format("YYYY-MM-DD"),
      year: today.subtract(1, "year").format("YYYY-MM-DD")
    }[selectedPeriod] || today.subtract(1, "month").format("YYYY-MM-DD");
    const {
      startSearchDate,
      endSearchDate,
      searchOrderStatus
    } = {
      startSearchDate: startDate,
      endSearchDate: endDate,
      searchOrderStatus: undefined
    };
    t1 = useQuery;
    t0 = AuthQueryOptions["get:/wello/v2/auth/home/gift/order"]({
      query: {
        page: 1,
        size: 100,
        endSearchDate,
        searchOrderStatus,
        startSearchDate
      }
    });
    $[0] = selectedPeriod;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0) {
    t2 = {
      ...t0,
      select: _temp2
    };
    $[3] = t0;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: apiData
  } = t1(t2);
  let t3;
  if ($[5] !== entry || $[6] !== isEventProcess) {
    t3 = () => {
      if (isEventProcess && !getIsRecommendPhotoReviewEventModalOpened() && entry === WEBVIEW_TYPE_ENTRY.WELLO) {
        setIsRecommendPhotoReviewModalOpen(true);
        setIsRecommendPhotoReviewEventModalOpened();
      }
    };
    $[5] = entry;
    $[6] = isEventProcess;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== entry || $[9] !== isEventProcess || $[10] !== webviewType) {
    t4 = [isEventProcess, webviewType, entry];
    $[8] = entry;
    $[9] = isEventProcess;
    $[10] = webviewType;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== apiData?.contents) {
    t5 = apiData?.contents || [];
    $[12] = apiData?.contents;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const allOrders = t5;
  let t6;
  if ($[14] !== allOrders || $[15] !== selectedStatus) {
    t6 = selectedStatus === "all" ? allOrders : allOrders.filter(order => {
      const normalizedStatus = order.orderStatusCd?.replace("GIFT_ORDER_STATUS_", "");
      return normalizedStatus === selectedStatus;
    });
    $[14] = allOrders;
    $[15] = selectedStatus;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const orders = t6;
  const isEmpty = orders.length === 0;
  let t7;
  if ($[17] !== selectedStatus) {
    t7 = STATUS_OPTIONS.find(option => option.value === selectedStatus)?.label || "\uC804\uCCB4\uC8FC\uBB38";
    $[17] = selectedStatus;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const statusLabel = t7;
  let t8;
  if ($[19] !== selectedPeriod) {
    t8 = PERIOD_OPTIONS.find(option_0 => option_0.value === selectedPeriod)?.label || "1\uAC1C\uC6D4";
    $[19] = selectedPeriod;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const periodLabel = t8;
  const handleProductClick = _temp3;
  let t10;
  let t11;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <PageNavigation title={"\uC8FC\uBB38\uB0B4\uC5ED"} isNotUsedHomeButton={true} donationHomeButton={true} />;
    t10 = <DesktopTitle className="mb-20">주문내역</DesktopTitle>;
    t11 = cn("mb-16 flex items-center justify-end gap-12 px-16", "mobile-tablet:px-20");
    $[21] = t10;
    $[22] = t11;
    $[23] = t9;
  } else {
    t10 = $[21];
    t11 = $[22];
    t9 = $[23];
  }
  let t12;
  if ($[24] !== orders.length) {
    t12 = <p className="mr-auto font-500 text-Gray-900 typo-body4">전체 {orders.length}개</p>;
    $[24] = orders.length;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setIsStatusDrawerOpen(true);
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== statusLabel) {
    t14 = <Filter label={statusLabel} onClick={t13} />;
    $[27] = statusLabel;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsPeriodDrawerOpen(true);
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== periodLabel) {
    t16 = <Filter label={periodLabel} onClick={t15} />;
    $[30] = periodLabel;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t12 || $[33] !== t14 || $[34] !== t16) {
    t17 = <section className={t11}>{t12}{t14}{t16}</section>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== isEmpty || $[37] !== orders) {
    t18 = isEmpty ? <EmptyState /> : <section className="flex flex-col gap-16 pb-20"><ul className={cn("flex flex-col gap-42 px-0", "mobile-tablet:px-16")}>{orders.map(order_1 => <li key={order_1.giftOrderId}><OrderCard order={order_1} onProductClick={() => handleProductClick(order_1)} /></li>)}</ul></section>;
    $[36] = isEmpty;
    $[37] = orders;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setIsStatusDrawerOpen(false);
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = value => {
      setSelectedStatus(value);
      setIsStatusDrawerOpen(false);
    };
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = STATUS_OPTIONS.map(_temp4);
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== selectedStatus) {
    t22 = <Radio.Group value={selectedStatus} onChange={t20} theme="black" className="grid grid-cols-2 gap-8">{t21}</Radio.Group>;
    $[42] = selectedStatus;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== isStatusDrawerOpen || $[45] !== t22) {
    t23 = <ResponsiveDialog opened={isStatusDrawerOpen} onClose={t19} title={"\uC8FC\uBB38\uC0C1\uD0DC \uC120\uD0DD"} drawerType="bottom">{t22}</ResponsiveDialog>;
    $[44] = isStatusDrawerOpen;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setIsPeriodDrawerOpen(false);
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = value_0 => {
      setSelectedPeriod(value_0);
      setIsPeriodDrawerOpen(false);
    };
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = PERIOD_OPTIONS.map(_temp5);
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== selectedPeriod) {
    t27 = <Radio.Group value={selectedPeriod} onChange={t25} theme="black" className="flex flex-col items-start gap-8">{t26}</Radio.Group>;
    $[50] = selectedPeriod;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== isPeriodDrawerOpen || $[53] !== t27) {
    t28 = <ResponsiveDialog opened={isPeriodDrawerOpen} onClose={t24} title={"\uC8FC\uBB38\uAE30\uD55C \uC120\uD0DD"} drawerType="bottom">{t27}</ResponsiveDialog>;
    $[52] = isPeriodDrawerOpen;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsRecommendPhotoReviewModalOpen(false);
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== isRecommendPhotoReviewModalOpen) {
    t30 = <RecommendPhotoReviewModal open={isRecommendPhotoReviewModalOpen} onClose={t29} />;
    $[56] = isRecommendPhotoReviewModalOpen;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== t17 || $[59] !== t18 || $[60] !== t23 || $[61] !== t28 || $[62] !== t30) {
    t31 = <section>{t9}{t10}{t17}{t18}{t23}{t28}{t30}</section>;
    $[58] = t17;
    $[59] = t18;
    $[60] = t23;
    $[61] = t28;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  return t31;
};
export default OrdersPage;
const SESSION_STORAGE_KEY = {
  /** 포토후기 이벤트 관련 모달 열었는지 여부 */
  RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED: {
    key: 'RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED',
    activeValue: 'true'
  } as const
} as const;
const getIsRecommendPhotoReviewEventModalOpened = () => {
  return sessionStorage.getItem(SESSION_STORAGE_KEY.RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED.key) === SESSION_STORAGE_KEY.RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED.activeValue;
};
const setIsRecommendPhotoReviewEventModalOpened = () => {
  sessionStorage.setItem(SESSION_STORAGE_KEY.RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED.key, SESSION_STORAGE_KEY.RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED.activeValue);
};
function _temp(state) {
  return state.webviewType;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  return context;
}
function _temp3(order_0) {
  const firstProduct = order_0.products?.[0];
  trackGtmEvent(GTM_EVENT_TYPE.SELECT_ITEM, {
    currency: "KRW",
    value: 0,
    local_name: order_0.homeInstitutionName,
    items: [{
      item_type: "\uB2F5\uB840\uD488",
      item_list_name: "\uC8FC\uBB38\uB0B4\uC5ED",
      item_list_id: order_0.giftOrderId,
      item_name: firstProduct?.productName ?? "",
      quantity: firstProduct?.quantity ?? 1,
      item_id: firstProduct?.productNumber ?? "",
      price: 0
    }]
  });
}
function _temp4(option_1) {
  return <Radio.Item key={option_1.value} value={option_1.value} className="w-full justify-start py-8">{option_1.label}</Radio.Item>;
}
function _temp5(option_2) {
  return <Radio.Item key={option_2.value} value={option_2.value} className="py-4">{option_2.label}</Radio.Item>;
}